import { Box, Card, Typography, Skeleton } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  AccreditationBreakdown,
  CategoryBreakdownItem,
  CertificateRollupItem,
  CertificatesSummaryData,
} from '../types'

interface ActivityBreakdownProps {
  data: CertificatesSummaryData | undefined
  isLoading: boolean
  hasLinkedService: boolean
  selectedType?: CertificateRollupItem['type'] | null
  onSelectType?: (type: CertificateRollupItem['type']) => void
  // Category breakdown for the currently selected type (hours only, no
  // percentage per design) — undefined/empty when no type is selected.
  categoryBreakdown?: CategoryBreakdownItem[]
  selectedCategory?: string | null
  onSelectCategory?: (categoryName: string) => void
  accreditationBreakdown?: AccreditationBreakdown
}

export default function ActivityBreakdown({
  data,
  isLoading,
  hasLinkedService,
  selectedType = null,
  onSelectType,
  categoryBreakdown = [],
  selectedCategory = null,
  onSelectCategory,
  accreditationBreakdown,
}: ActivityBreakdownProps) {
  let theme = useTheme()

  if (isLoading) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Activity Types
        </Typography>
        <Box
          className={
            hasLinkedService
              ? 'grid grid-cols-1 md:grid-cols-2 gap-4'
              : 'grid grid-cols-1 gap-4'
          }
        >
          <Skeleton
            variant="rectangular"
            sx={{
              height: 300,
              borderRadius: 1,
            }}
          />
          {hasLinkedService && (
            <Skeleton
              variant="rectangular"
              sx={{
                height: 300,
                borderRadius: 1,
              }}
            />
          )}
        </Box>
      </Box>
    )
  }

  if (!data) {
    return null
  }

  let activityTypeColors = {
    recorded: theme.palette.analyticsColors.recordedCourses,
    live: theme.palette.analyticsColors.liveClasses,
    external: theme.palette.analyticsColors.externalCerts,
    event: theme.palette.analyticsColors.events,
    attendance: theme.palette.analyticsColors.activities,
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Activity Types
      </Typography>

      <Box
        className={
          hasLinkedService
            ? 'grid grid-cols-1 md:grid-cols-2 gap-4'
            : 'grid grid-cols-1 gap-4'
        }
      >
        <Card
          sx={{
            p: 3,
            backgroundColor: 'background.paperDark',
            border: '1px solid',
            borderColor: 'divider',
            height: '100%',
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Activity Distribution
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {data.activityBreakdown.map((item) => {
              let label =
                item.activityType === 'recorded'
                  ? 'Recorded Courses'
                  : item.activityType === 'live'
                    ? 'Live Classes'
                    : item.activityType === 'external'
                      ? 'External Certificates'
                      : item.activityType === 'event'
                        ? 'Events'
                        : 'Attendance Only'
              let isSelectable = Boolean(onSelectType)
              let isSelected = selectedType === item.activityType
              return (
                <Box
                  key={item.activityType}
                  component={isSelectable ? 'button' : 'div'}
                  type={isSelectable ? 'button' : undefined}
                  aria-pressed={isSelectable ? isSelected : undefined}
                  aria-label={
                    isSelectable
                      ? `Show ${label} in the records table below`
                      : undefined
                  }
                  onClick={
                    isSelectable
                      ? () => onSelectType?.(item.activityType)
                      : undefined
                  }
                  sx={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    p: 1,
                    m: 0,
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'transparent',
                    borderRadius: 1,
                    backgroundColor: isSelected
                      ? 'action.selected'
                      : 'transparent',
                    color: 'inherit',
                    font: 'inherit',
                    cursor: isSelectable ? 'pointer' : 'default',
                    transition: 'background-color 0.15s ease',
                    '&:hover': isSelectable
                      ? { backgroundColor: 'action.hover' }
                      : undefined,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ textTransform: 'capitalize' }}
                    >
                      {label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {item.count} ({item.percentage.toFixed(0)}%)
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      height: 6,
                      backgroundColor: 'background.default',
                      borderRadius: 3,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${item.percentage}%`,
                        backgroundColor:
                          activityTypeColors[item.activityType] ||
                          theme.palette.primary.main,
                      }}
                    />
                  </Box>
                </Box>
              )
            })}
          </Box>

          {selectedType && onSelectCategory && categoryBreakdown.length > 0 && (
            <Box
              sx={{
                mt: 3,
                pt: 3,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Category Breakdown
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {categoryBreakdown.map((item) => {
                  let isSelected = selectedCategory === item.categoryName
                  return (
                    <Box
                      key={item.categoryName}
                      component="button"
                      type="button"
                      aria-pressed={isSelected}
                      aria-label={`Show ${item.categoryName} in the records table below`}
                      onClick={() => onSelectCategory(item.categoryName)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        minHeight: 44,
                        textAlign: 'left',
                        p: 1,
                        m: 0,
                        border: '1px solid',
                        borderColor: isSelected
                          ? 'primary.main'
                          : 'transparent',
                        borderRadius: 1,
                        backgroundColor: isSelected
                          ? 'action.selected'
                          : 'transparent',
                        color: 'inherit',
                        font: 'inherit',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <Typography variant="body2">
                        {item.categoryName}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {item.totalHours.toFixed(1)} hrs
                      </Typography>
                    </Box>
                  )
                })}
              </Box>
            </Box>
          )}
        </Card>

        {hasLinkedService && (
          <Card
            sx={{
              p: 3,
              backgroundColor: 'background.paperDark',
              border: '1px solid',
              borderColor: 'divider',
              height: '100%',
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Assignment Context
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">Assigned Activities</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {data.assignmentStats.assignedCompletions}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    height: 6,
                    backgroundColor: 'background.default',
                    borderRadius: 3,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      width: `${(data.assignmentStats.assignedCompletions / (data.assignmentStats.assignedCompletions + data.assignmentStats.selfPacedCompletions)) * 100}%`,
                      backgroundColor:
                        theme.palette.analyticsColors.assignedActivities,
                    }}
                  />
                </Box>
              </Box>

              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">Self-Paced Learning</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {data.assignmentStats.selfPacedCompletions}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    height: 6,
                    backgroundColor: 'background.default',
                    borderRadius: 3,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      width: `${(data.assignmentStats.selfPacedCompletions / (data.assignmentStats.assignedCompletions + data.assignmentStats.selfPacedCompletions)) * 100}%`,
                      backgroundColor:
                        theme.palette.analyticsColors.selfPacedLearning,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Card>
        )}
      </Box>

      {accreditationBreakdown &&
        (accreditationBreakdown.accreditedHours > 0 ||
          accreditationBreakdown.notAccreditedHours > 0) && (
          <Card
            sx={{
              mt: 2,
              p: 3,
              backgroundColor: 'background.paperDark',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Accreditation (Courses & Events)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {(
                [
                  {
                    label: 'Accredited',
                    hours: accreditationBreakdown.accreditedHours,
                    color: theme.palette.success.main,
                  },
                  {
                    label: 'Not Accredited',
                    hours: accreditationBreakdown.notAccreditedHours,
                    color: theme.palette.grey[500],
                  },
                ] as const
              ).map((entry) => (
                <Box key={entry.label}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">{entry.label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {entry.hours.toFixed(1)} hrs
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      height: 6,
                      backgroundColor: 'background.default',
                      borderRadius: 3,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${
                          (entry.hours /
                            (accreditationBreakdown.accreditedHours +
                              accreditationBreakdown.notAccreditedHours || 1)) *
                          100
                        }%`,
                        backgroundColor: entry.color,
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Card>
        )}
    </Box>
  )
}
